import Icon, { LoadingOutlined } from '@ant-design/icons';
import SortArrows from '@/assets/icons/chevron-selector-vertical.svg?react';
import Arrow from '@/assets/icons/chevron-right.svg?react';
import ArrowLeft from '@/assets/icons/chevron-left.svg?react';
import { Empty, Flex, Spin, Table } from 'antd';
import { ColumnsType, TablePaginationConfig } from 'antd/es/table';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import styles from './surveys-table.module.scss';
import { Survey } from '@/services/surveys-service/surveys-service.types';
import { Score } from '../score/score';
import { SurveyStatus } from '../survey-status/survey-status';
import { generateDataSource } from './surveys-table.utils';
import { ShowMoreButton } from '../show-more-button/show-more-button';
import { DetailsLink } from '../details-link/details-link';
import { useScreenSize } from '@/hooks/use-screen-size/use-screen-size';
import { useConversationTypeOptions } from '../surveys-header/surveys-header.constants';
import { Pagination } from '@/services/request/request.types';
import { OrderBy } from '@/hooks/use-sort-and-pagination/use-sort-and-pagination.types';
import { PAGE_SIZE_OPTIONS } from '@/hooks/use-sort-and-pagination/use-sort-and-pagination';
import { FilterValue, SorterResult } from 'antd/es/table/interface';
import { calculateDateFromISOtoYYYY_MM_DD, calculateHourFromISODate } from '@/utils/helpers';

interface SurveysTableProps {
  surveysData?: Survey[];
  isAdmin?: boolean;
  pagination?: Pagination;
  onChange: (page?: number, perPage?: number, sortBy?: string, orderBy?: OrderBy) => void;
  currentPage: number;
  loading?: boolean;
}

export const SurveysTable = ({
  surveysData,
  isAdmin,
  pagination,
  onChange,
  currentPage,
  loading,
}: SurveysTableProps) => {
  const { t } = useTranslation('surveys');
  const conversationTypeOptions = useConversationTypeOptions();
  const dataSource = generateDataSource(surveysData || []);
  const screenSize = useScreenSize();
  const isDesktop = screenSize.width >= 1024;

  const handleChange = (
    pagination: TablePaginationConfig,
    _: Record<string, FilterValue | null>,
    sorter: SorterResult<Survey> | SorterResult<Survey>[]
  ) => {
    if (!Array.isArray(sorter)) {
      sorter = [sorter];
    }

    const sort = sorter.find((s) => s.order);

    const sortBy = sort?.field?.toString();
    const orderBy = sort ? (sort.order === 'ascend' ? 'asc' : 'desc') : undefined;

    onChange(pagination.current, pagination.pageSize, sortBy, orderBy);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const columns: ColumnsType<Survey> = [
    ...(isAdmin
      ? [
          {
            title: t('fields.company'),
            dataIndex: 'company_name',
            key: 'company',
            className: styles.companyContainer,
            render: (company_name: Survey['company_name']) => <div>{company_name || ''}</div>,
          },
        ]
      : []),
    {
      title: t('fields.phone'),
      dataIndex: 'phone',
      key: 'phone',
      className: styles.phoneNumberContainer,
      // A row whose call no longer resolves has no conversation to open, and a link built
      // from a null id navigates to a detail page for the string "null".
      render: (phone: Survey['phone'], record) => {
        if (!record?.conversation_id) return <span>{phone ?? '---'}</span>;

        return (
          <Link to={`/conversations/${record.conversation_id}`} className={styles.phoneNumber}>
            {phone ?? '---'}
          </Link>
        );
      },
    },
    {
      width: 150,
      title: t('fields.dateTime'),
      dataIndex: 'created_at',
      key: 'created_at',
      className: styles.dateAndTimeContainer,
      render: (created_at: Survey['created_at']) => (
        <Flex vertical>
          <Flex wrap="wrap">
            <p className={styles.date}>{calculateDateFromISOtoYYYY_MM_DD(created_at)}</p>
            <span className={styles.circle}>&#9679;</span>
            <p className={styles.hour}>{calculateHourFromISODate(created_at)}</p>
          </Flex>
        </Flex>
      ),
    },
    {
      title: t('fields.smsContent'),
      dataIndex: 'body',
      key: 'body',
      className: styles.bodyContainer,
      render: (smsContent: Survey['body']) =>
        !smsContent ? (
          <p>---</p>
        ) : (
          <div>
            {isDesktop ? <p>{smsContent}</p> : <ShowMoreButton text={smsContent} maxChars={76} />}
          </div>
        ),
    },
    {
      title: t('fields.type'),
      dataIndex: 'type',
      key: 'type',
      sorter: true,
      className: styles.typeContainer,
      render: (type: Survey['type']) => {
        const typeLabel = conversationTypeOptions.find((option) => option.value === type)?.label;
        return <p>{typeLabel || type || '---'}</p>;
      },
      sortIcon: () => (
        <Icon
          component={() => <SortArrows stroke={styles.arrowColorIcon} width={14} height={14} />}
        />
      ),
    },

    {
      title: t('fields.score'),
      dataIndex: 'score',
      key: 'score',
      className: styles.scoreContainer,
      render: (score: Survey['score']) => <Score className={styles.statusChip} score={score} />,
    },
    {
      title: t('fields.status'),
      dataIndex: 'status',
      key: 'status',
      className: styles.statusContainer,
      render: (status: Survey['status']) => (
        <SurveyStatus className={styles.statusChip} variant={status} />
      ),
    },
    {
      title: t('fields.conversation'),
      dataIndex: 'conversation_id',
      key: 'conversation_id',
      className: styles.statusContainer,
      render: (conversation_id: Survey['conversation_id']) =>
        conversation_id ? <DetailsLink link={`/conversations/${conversation_id}`} /> : <p>---</p>,
    },
  ];
  return (
    <Table
      pagination={{
        current: currentPage,
        position: ['bottomCenter'],
        pageSizeOptions: PAGE_SIZE_OPTIONS.map(String),
        showSizeChanger: true,
        defaultPageSize: pagination?.pageItems,
        total: pagination?.totalCount,
        locale: { items_per_page: '' },
        nextIcon: <Icon component={() => <Arrow strokeWidth={1} width={16} height={16} />} />,
        prevIcon: <Icon component={() => <ArrowLeft strokeWidth={2} width={16} height={16} />} />,
      }}
      onChange={handleChange}
      scroll={{ x: 800 }}
      className={styles.table}
      dataSource={dataSource}
      columns={columns}
      locale={{
        emptyText: loading ? (
          <div style={{ padding: '40px 0', textAlign: 'center' }}>
            <Spin indicator={<LoadingOutlined spin />} />
          </div>
        ) : (
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
        ),
      }}
    />
  );
};
